import { Stack } from "@mantine/core"
import { MultiSelect, LabelWithTooltip, SectionHeader } from "@tfg/ui"
import { useTranslation } from "react-i18next"
import type { z } from "zod"

import { ContentFilteringProps } from "@tfg/types/types/communication-studio/configurations.types"

import { DataFetchWrapper } from "~/components/DataFetchWrapper"
import { useFindStandardsByClientId } from "~/gen/api/hooks/useFindStandardsByClientId"

export interface StandardsConfigurationsRendererProps {
  readonly standardsToBeShown?: number[]
  readonly clientId: string
  readonly validationErrors?: z.ZodError
  readonly updateModalConfigurations?: (
    configurations: Partial<ContentFilteringProps> | null
  ) => void
}

export const StandardsConfigurationsRenderer = ({
  standardsToBeShown,
  clientId,
  validationErrors,
  updateModalConfigurations,
}: StandardsConfigurationsRendererProps) => {
  const { t } = useTranslation("communicationStudio")
  const query = useFindStandardsByClientId(clientId, {
    query: {
      enabled: !!clientId,
    },
  })

  const handleStandardsChange = (value: string[]) => {
    if (!updateModalConfigurations) {
      return
    }

    updateModalConfigurations({
      standardsToBeShown: value.map((v) => parseInt(v, 10)),
    })
  }

  return (
    <DataFetchWrapper query={query}>
      {({ data }) => (
        <Stack gap={1}>
          <SectionHeader
            order={6}
            title={t("tabs.content.contentFiltering.standardsToShow.title")}
            subtitle={t(
              "tabs.content.contentFiltering.standardsToShow.subtitle"
            )}
            mb="xs"
            gap={1}
            titleFw={600}
          />
          <MultiSelect
            label={
              <LabelWithTooltip
                label={t("tabs.content.contentFiltering.standardsToShow.label")}
                hoverHelperText={t(
                  "tabs.content.contentFiltering.standardsToShow.labelTooltip"
                )}
              />
            }
            labelProps={{
              w: "100%",
            }}
            size="xs"
            searchable
            placeholder={t(
              "tabs.content.contentFiltering.standardsToShow.placeholder"
            )}
            emptyMessage={t(
              "tabs.content.contentFiltering.standardsToShow.emptyMessage"
            )}
            value={
              standardsToBeShown
                ? standardsToBeShown.map((standard) => standard.toString())
                : []
            }
            data={data?.data.map((standard) => ({
              value: standard.id.toString(),
              label: standard.name,
            }))}
            miw={300}
            onChange={handleStandardsChange}
            error={
              validationErrors
                ? t("contentFiltering.errors.standardsToShow")
                : undefined
            }
          />
        </Stack>
      )}
    </DataFetchWrapper>
  )
}
